import {
  AnyAppPath,
  AppBaseNamespaces,
  EnvironmentPagePath,
  getSlug,
  PORTAL_BASE_SLUG,
  replacePathParams
} from '~/components/AppRouter/utils.ts';
import { Params, useMatch } from '@solidjs/router';
import { ZodError } from 'zod';
import { globalStore, setGlobalStore } from '~/stores';
import {
  CLIENT_SECRET_HASH_LENGTH,
  miscConstants,
  timeSignatures
} from '~/consts.ts';
import { initPosthog } from '~/utils/productLogging/posthog.ts';
import { initSentry } from '~/utils/productLogging/sentry.ts';
import { initClarity } from '~/utils/productLogging/clarity';
import logger from '~/utils/logger';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import kebabcaseKeys from 'kebabcase-keys';
import { jwtDecode } from 'jwt-decode';
import { getPortalFeaturesToDisplayFromQueryParams } from '~/pages/AdminPortal/utils.ts';
import { ClientSecretData } from '~/api';
import printEasterEgg from '~/utils/easterEgg.ts';
import { INTERNAL_USER_EMAIL_PATTERNS } from '~/utils/internalUsers.ts';

enum AppContexts {
  SCALEKIT_DASHBOARD = 'SCALEKIT_DASHBOARD',
  CUSTOMER_PORTAL = 'CUSTOMER_PORTAL',
  HANDLERS = 'HANDLERS',
  HOSTED_AUTH_PAGES = 'HOSTED_AUTH_PAGES',
  ENV_APP_PAGES = 'ENV_APP_PAGES'
}

const isDevelopmentMode = () => import.meta.env.MODE === 'development';

const isStagingMode = () => import.meta.env.MODE === 'staging';

function isScalekitWorkspaceEnvironment() {
  return /^app(?:\.[a-zA-Z0-9-]+)?\.scalekit\./.test(window.location.hostname);
}

function isScalekitPlatformEnvironment() {
  return /^auth(?:\.[a-zA-Z0-9-]+)?\.scalekit\./.test(window.location.hostname);
}

const isScalekitPlatformProductionEnvironment = (): boolean => {
  return (
    import.meta.env.MODE !== 'production' && isScalekitPlatformEnvironment()
  );
};

const sleep = (delayInMs: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, delayInMs);
  });
};

function getAppContext(): AppContexts {
  const pathName = new URL(window.location.href).pathname;
  if (pathName.startsWith(`${AppBaseNamespaces.WORKSPACE}/`)) {
    return AppContexts.SCALEKIT_DASHBOARD;
  } else if (pathName.startsWith(`${PORTAL_BASE_SLUG}/`)) {
    return AppContexts.CUSTOMER_PORTAL;
  } else if (pathName.startsWith(`${AppBaseNamespaces.HANDLERS}/`)) {
    return AppContexts.HANDLERS;
  } else if (pathName.startsWith(`${AppBaseNamespaces.HOSTED_AUTH_PAGES}/`)) {
    return AppContexts.HOSTED_AUTH_PAGES;
  } else if (pathName.startsWith(`${AppBaseNamespaces.ENV_APP_PAGES}/`)) {
    return AppContexts.ENV_APP_PAGES;
  }
  throw new Error('Invalid namespace');
}

function isDebugLocalStorageKeyOn() {
  return window.localStorage.getItem('DEBUG_MODE') === 'on';
}

function getRandomInteger(min: number, max: number): number {
  // Using Math.floor to round down to the nearest integer
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function searchInListByKeys(
  list: { [key: string]: any }[],
  keys: string[],
  search: string
): Array<object> {
  return list.filter((item) => {
    return keys.some(
      (k) => !!item[k] && item[k].toLowerCase().includes(search.toLowerCase())
    );
  });
}

const flattenObject = (
  input: Record<string, any>,
  keyName?: string,
  delimeter: string = '.'
): Record<string, any> => {
  let result: Record<string, any> = {};

  for (const key in input) {
    const newKey = keyName ? `${keyName}${delimeter}${key}` : key;

    if (typeof input[key] === 'object' && !Array.isArray(input[key])) {
      result = { ...result, ...flattenObject(input[key], newKey, delimeter) };
    } else {
      result[newKey] = input[key];
    }
  }

  return result;
};

const deepExtendObjects = (
  obj1: Record<string, any>,
  obj2: Record<string, any>
): Record<string, any> => {
  let merged: Record<string, any> = { ...obj1 };
  for (const key in obj2) {
    if (typeof obj2[key] === 'object' && !Array.isArray(obj2[key])) {
      const firstObjectPart = obj1[key] || {};
      merged[key] = {
        ...firstObjectPart,
        ...deepExtendObjects(firstObjectPart, obj2[key])
      };
    } else {
      merged[key] = obj2[key];
    }
  }
  return merged;
};

const getNameInitials = (name: string = '', includeLastName = false): string =>
  includeLastName
    ? String(name)
        .split(' ')
        .map((part) => part.substring(0, 1).toUpperCase())
        .join('')
        .substring(0, 2)
    : String(name).substring(0, 1).toUpperCase();

const getRandomId = (): string =>
  Date.now().toString(36) + Math.random().toString(36).substring(2);

const getAppliedClasses = (classList: {
  [k: string]: boolean | undefined;
}): string => {
  return Object.keys(classList)
    .filter((className) => classList[className])
    .join(' ');
};

const getPathParams = (routePathPattern: string): { [key: string]: string } => {
  const urlPath = new URL(location.href).pathname;
  const patternParts = routePathPattern.split('/');
  const urlPathParts = urlPath.split('/');
  const params: { [key: string]: string } = {};
  patternParts.forEach((part, index) => {
    if (part.startsWith(':')) {
      const paramName = part.replace(':', '');
      params[paramName] = urlPathParts[index];
    }
  });
  return params;
};

const cloneObject = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

const arrayToObjectByKey = (list: any[], key: string) => {
  return list.reduce((obj, item) => {
    if (item[key]) {
      obj[item[key]] = item;
    }
    return obj;
  }, {});
};

type GetFullPathWithoutParamsType = {
  paths: string[];
  params: Params;
  pathGenerator?: (path: string) => string;
};

// accept paths as a list of route path definition
// and return the one that matches (if any) the current route
function getFullPathWithoutParams({
  paths,
  params
}: GetFullPathWithoutParamsType): string {
  for (let path of paths) {
    const composedPath = getSlug.environmentById(
      params.environmentId,
      replacePathParams(path as AnyAppPath, params) as EnvironmentPagePath,
      true
    );
    const match = useMatch(() => composedPath);
    if (match()) {
      return getSlug.environmentById(
        undefined,
        replacePathParams(path as AnyAppPath, {}) as EnvironmentPagePath,
        true
      );
    }
  }
  return '';
}

function interpolateVarsInString(str: string, vars: Record<string, string>) {
  let preparedString = str;
  Object.keys(vars).forEach((param) => {
    preparedString = preparedString.replace(`\{${param}\}`, vars[param]);
  });
  return preparedString;
}

function getErrorObjectFromZod(errors: ZodError): Record<string, string> {
  return errors.issues.reduce((errorObj, currentError) => {
    let obj: Record<any, any> = {};
    obj[(currentError.path || []).join('.')] = currentError.message;
    return {
      ...errorObj,
      ...obj
    };
  }, {});
}

const popupCenter = ({ url, title, w, h }) => {
  // Fixes dual-screen position                             Most browsers      Firefox
  const dualScreenLeft =
    window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop =
    window.screenTop !== undefined ? window.screenTop : window.screenY;

  const width = window.innerWidth
    ? window.innerWidth
    : document.documentElement.clientWidth
      ? document.documentElement.clientWidth
      : screen.width;
  const height = window.innerHeight
    ? window.innerHeight
    : document.documentElement.clientHeight
      ? document.documentElement.clientHeight
      : screen.height;

  const systemZoom = width / window.screen.availWidth;
  const left = (width - w) / 2 / systemZoom + dualScreenLeft;
  const top = (height - h) / 2 / systemZoom + dualScreenTop;
  const newWindow = window.open(
    url,
    title,
    `
      scrollbars=yes,
      width=${w / systemZoom}, 
      height=${h / systemZoom}, 
      top=${top}, 
      left=${left}
      `
  );

  if (window.onfocus) newWindow?.focus();
  return newWindow;
};

function openLinkInNewTab(link: string) {
  (window as any).open(link, '_blank').focus();
}

function setOnboardingState() {
  const onBoardingParamValue = getQueryParam(
    miscConstants.QUERY_PARAMS.ONBOARDING_PARAM_NAME
  );
  if (onBoardingParamValue === 'true') {
    setGlobalStore('shouldShowQuickStartModalOnLoad', true);
  }
}

function initAppCommons() {
  logger.info(import.meta.env);
  initSentry();
  let appContext: AppContexts;
  try {
    appContext = getAppContext();
  } catch (e) {
    appContext = isScalekitWorkspaceEnvironment()
      ? AppContexts.SCALEKIT_DASHBOARD
      : AppContexts.HOSTED_AUTH_PAGES;
  }
  setGlobalStore('appContext', appContext);

  initPosthog();
  initClarity();

  if (appContext === AppContexts.SCALEKIT_DASHBOARD) {
    printEasterEgg();
    setOnboardingState();
    setGlobalStore('appFavIcon', `${getCdnBaseUrl()}scalekit-icon.svg`);
  } else if (appContext === AppContexts.CUSTOMER_PORTAL) {
    setGlobalStore(
      'adminPortalEnabledModulesFromQueryParam',
      getPortalFeaturesToDisplayFromQueryParams()
    );
  }
}

function withPortalAuthToken(path: string): string {
  if (
    globalStore.shouldForwardToken &&
    globalStore.appContext === AppContexts.CUSTOMER_PORTAL &&
    globalStore.portalAuthToken
  ) {
    const pathParts = path.split('?');
    const params = new URLSearchParams(
      pathParts.length > 1 ? pathParts[1] : ''
    );
    params.set(
      miscConstants.QUERY_PARAMS.CUSTOMER_PORTAL_AUTH_TOKEN,
      globalStore.portalAuthToken
    );
    return `${pathParts[0]}?${params.toString()}`;
  }
  return path;
}

type PollAtIntervalParamType = {
  intervalInMs: number;
  maxAttempts?: number;
  pollFn: () => Promise<boolean>;
  onMaxAttemptsReached?: () => void;
};
const getIntervalPoller = () => {
  let continuePolling = true;
  return {
    start: async ({
      intervalInMs,
      maxAttempts,
      pollFn,
      onMaxAttemptsReached
    }: PollAtIntervalParamType) => {
      let attempts = 0;
      let success = false;
      while (
        continuePolling &&
        ((maxAttempts && attempts < maxAttempts) || !maxAttempts)
      ) {
        // poll indefinitely if maxAttempts is not set
        success = await pollFn();
        if (success) {
          break;
        }
        attempts++;
        await new Promise((resolve) => setTimeout(resolve, intervalInMs));
      }
      if (
        !success &&
        maxAttempts &&
        attempts >= maxAttempts &&
        onMaxAttemptsReached
      ) {
        onMaxAttemptsReached();
      }
    },
    stop: () => {
      continuePolling = false;
    }
  };
};

function getUniqueId(): string {
  return Math.random().toString(36).substring(2, 11);
}

function isLoadedInIframe(): boolean {
  try {
    return window.parent.location.origin !== window.location.origin;
  } catch (e) {
    return true;
  }
}

function getAppBaseUrl() {
  if (import.meta.env.MODE !== 'production') {
    return import.meta.env.VITE_APP_HOST;
  }
  return `${window.location.protocol}//${window.location.host}`;
}

function getCdnBaseUrl() {
  return import.meta.env.BASE_URL.endsWith('/')
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`;
}

function getCamelCase(str: string): string {
  return Object.keys(camelcaseKeys({ [str]: true }))[0];
}

function getSnakeCase(str: string): string {
  return Object.keys(snakecaseKeys({ [str]: true }))[0];
}

function getKebabCase(str: string): string {
  return Object.keys(kebabcaseKeys({ [str]: true }))[0];
}

function copyTextIntoClipboard(text: string): Promise<boolean> {
  return new Promise((resolve, reject) => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(text)
        .then(() => resolve(true))
        .catch((error) => {
          logger.error('Clipboard write failed:', error);
          reject(error);
        });
    }
  });
}

const mergeEmailParts = (emailParts: Array<string>) => emailParts.join('@');

const base64Decode: (str: string) => Record<any, any> = (str: string) =>
  jwtDecode(str, { header: true });

const removeParamFromUrl = (param: string) => {
  const url = new URL(window.location.href);
  url.searchParams.delete(param);
  window.history.replaceState({}, '', url.toString());
};

const getFileExtensionFromName = (name: string) => name.split('.').pop();

const isNotNullOrEmpty = (value: any): boolean =>
  value !== null && value !== undefined && value !== '';

const deepEqual = <T>(obj1: T, obj2: T): boolean => {
  // Check if both values are the same
  if (obj1 === obj2) {
    return true;
  }

  // Check if both are objects (and not null)
  if (
    typeof obj1 !== 'object' ||
    obj1 === null ||
    typeof obj2 !== 'object' ||
    obj2 === null
  ) {
    return false;
  }

  // Check if they have the same number of keys
  const keys1 = Object.keys(obj1) as Array<keyof T>;
  const keys2 = Object.keys(obj2) as Array<keyof T>;

  if (keys1.length !== keys2.length) {
    return false;
  }

  // Check each key in obj1 and compare values in both objects
  for (let key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

function getQueryParam(param: string) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param); // Returns the value of the parameter or null if not found
}

function addQueryStringToUri(uri: string, queryString: string): string {
  if (!uri) {
    throw new Error('URI cannot be empty');
  }

  if (!queryString) {
    return uri;
  }

  const hasQuery = uri.includes('?');
  const endsWithQuestionOrAmp = uri.endsWith('?') || uri.endsWith('&');

  if (endsWithQuestionOrAmp) {
    return `${uri}${queryString}`;
  }

  return `${uri}${hasQuery ? '&' : '?'}${queryString}`;
}

function capitalizeStr(str: string): string {
  return str.substring(0, 1).toUpperCase() + str.substring(1).toLowerCase();
}

const ENTER_KEY = 'Enter';
function isEnterKeyEvent(e: KeyboardEvent) {
  return e.key === ENTER_KEY;
}

function isValidHexColor(hex: string): boolean {
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexRegex.test(hex);
}

const getMaskedSecret = (secret: ClientSecretData): string => {
  const maskedPrefix = '*'.repeat(
    CLIENT_SECRET_HASH_LENGTH - (secret.secretSuffix?.length || 0)
  );
  return secret.secretSuffix
    ? maskedPrefix + secret.secretSuffix
    : maskedPrefix;
};

const getRandomColor = (() => {
  function* cyclicColorGenerator() {
    const colors = [
      '#FEEBE7',
      '#FCEFFC',
      '#FFF8C6',
      '#F5F2FF',
      '#F0F4FF',
      '#E2F9FF',
      '#DDFBF3',
      '#FFEDD5',
      '#E9F9EE',
      '#E7F9FB',
      '#EDF6FF',
      '#FFF3D0',
      '#EDFADA',
      '#E1F3FF',
      '#EDEDFF'
    ];

    let index = Math.floor(Math.random() * colors.length); // random starting index
    while (true) {
      yield colors[index % colors.length];
      index++;
    }
  }
  const colorGen = cyclicColorGenerator();
  return (): string => colorGen.next().value as string;
})();

const getTranslationKeyFunction = (basePath: string) => (path: string) =>
  `${basePath}.${path}`;

const pluralize = (word: string): string => {
  if (!word) return '';

  const lowerWord = word.toLowerCase();
  const isUpper = word === word.toUpperCase();
  const isCapitalized = word[0] === word[0].toUpperCase();

  let plural: string;

  if (lowerWord.match(/[^aeiou]y$/i)) {
    // Ends with consonant + y → ies
    plural = word.slice(0, -1) + 'ies';
  } else if (lowerWord.match(/(s|x|z|ch|sh)$/i)) {
    // Ends with s, x, z, ch, sh → es
    plural = word + 'es';
  } else if (lowerWord.endsWith('f')) {
    // Ends with f → ves
    plural = word.slice(0, -1) + 'ves';
  } else if (lowerWord.endsWith('fe')) {
    // Ends with fe → ves
    plural = word.slice(0, -2) + 'ves';
  } else {
    // Default case → add s
    plural = word + 's';
  }

  // Preserve capitalization
  if (isUpper) return plural.toUpperCase();
  if (isCapitalized) return capitalizeStr(plural);
  return plural.toLowerCase();
};

const pluralizeUnit = (unit: string, count: number): string => {
  return count === 1 ? unit : pluralize(unit);
};

function formatNumberShort(n: number): string {
  if (n === null || n === undefined || isNaN(n)) return '';

  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';

  if (abs >= 1_000_000) {
    return `${sign}${(abs / 1_000_000).toFixed(abs % 1_000_000 === 0 ? 0 : 1)}Mn`;
  }

  if (abs >= 1_000) {
    return `${sign}${(abs / 1_000).toFixed(abs % 1_000 === 0 ? 0 : 1)}K`;
  }

  return `${n}`;
}

const isInternalUser = (() => {
  const cache = new Map<string, boolean>();

  return (email: string): boolean => {
    if (cache.has(email)) {
      return cache.get(email)!;
    }
    const result = INTERNAL_USER_EMAIL_PATTERNS.some((pattern) =>
      pattern.test(email)
    );
    cache.set(email, result);
    return result;
  };
})();

async function waitForElement(selector: string) {
  const pollIntervalInMs = 100;
  const poller = getIntervalPoller();
  await poller.start({
    intervalInMs: pollIntervalInMs,
    maxAttempts: timeSignatures.SEC_IN_MILLIS / pollIntervalInMs,
    pollFn: async () => {
      try {
        return !!document.querySelectorAll(selector).length;
      } catch (_e) {
        return false;
      }
    }
  });
}

function removeKeysWithSpecificValuesFromObject<T extends Record<string, any>>(
  obj: T,
  values: readonly unknown[]
): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => !values.includes(v))
  ) as Partial<T>;
}

export {
  isDevelopmentMode,
  isStagingMode,
  isScalekitWorkspaceEnvironment,
  isScalekitPlatformEnvironment,
  isScalekitPlatformProductionEnvironment,
  sleep,
  AppContexts,
  getAppContext,
  isDebugLocalStorageKeyOn,
  getRandomInteger,
  searchInListByKeys,
  flattenObject,
  deepExtendObjects,
  getNameInitials,
  getRandomId,
  getAppliedClasses,
  getPathParams,
  cloneObject,
  arrayToObjectByKey,
  getFullPathWithoutParams,
  interpolateVarsInString,
  getErrorObjectFromZod,
  popupCenter,
  openLinkInNewTab,
  initAppCommons,
  withPortalAuthToken,
  getIntervalPoller,
  getUniqueId,
  isLoadedInIframe,
  getAppBaseUrl,
  getCdnBaseUrl,
  getCamelCase,
  getSnakeCase,
  getKebabCase,
  copyTextIntoClipboard,
  mergeEmailParts,
  base64Decode,
  removeParamFromUrl,
  getFileExtensionFromName,
  isNotNullOrEmpty,
  deepEqual,
  getQueryParam,
  addQueryStringToUri,
  capitalizeStr,
  isEnterKeyEvent,
  isValidHexColor,
  getMaskedSecret,
  getRandomColor,
  getTranslationKeyFunction,
  pluralize,
  pluralizeUnit,
  formatNumberShort,
  isInternalUser,
  waitForElement,
  removeKeysWithSpecificValuesFromObject
};
